import { FC } from 'react';
import { Box, Chip, Grid, LinearProgress, Typography } from '@mui/material';
import { SubscriptionType, UserDetail } from '../../../types/user';
import { levelFromXp, levelProgress } from '../../../utils/userLevel';
import { fmtDate, fmtDateOnly } from '../userShowUtils';
import { KpiCard } from '../userShowComponents';

const SUBSCRIPTION_TYPE_LABELS: Record<SubscriptionType, string> = {
    [SubscriptionType.FREE]: '무료',
    [SubscriptionType.MONTHLY]: '월간',
    [SubscriptionType.YEARLY]: '연간',
    [SubscriptionType.LIFETIME]: '평생',
};

/**
 * ③ 활동 요약 스트립 — "이 유저가 실제로 뭘 했나"를 한눈에 보는 지표 타일 묶음.
 */
const ActivitySummaryStrip: FC<{ user: UserDetail }> = ({ user }) => {
    const lv = levelFromXp(user.totalXp);
    const progress = levelProgress(user.totalXp, lv);
    const wb = user.wordbookStats;
    const study = user.studyStats;
    const credit = user.aiCreditBalance;
    const eppi = user.eppiSummary;

    return (
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KpiCard
                    label={`Lv.${lv.level} · ${lv.nameKo}`}
                    value={user.xpBalance.toLocaleString()}
                    suffix="깃털 잔액"
                    extra={
                        <Box>
                            <LinearProgress variant="determinate" value={progress * 100} sx={{ mt: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                                누적 {user.totalXp.toLocaleString()} 깃털 · {lv.cefrLabel}
                            </Typography>
                        </Box>
                    }
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KpiCard
                    label="마스터 단어"
                    value={user.masteredWordCount.toLocaleString()}
                    suffix="개"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KpiCard
                    label="단어장"
                    value={(wb?.totalWordbooks ?? 0).toLocaleString()}
                    suffix="개"
                    extra={
                        <Typography variant="caption" color="text.secondary">
                            진행 중 {(wb?.inProgressWordbooks ?? 0).toLocaleString()} · 완료 {(wb?.completedWordbooks ?? 0).toLocaleString()}
                        </Typography>
                    }
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KpiCard
                    label="학습"
                    value={(study?.totalSessions ?? 0).toLocaleString()}
                    suffix="세션"
                    extra={
                        <Typography variant="caption" color="text.secondary">
                            최근 7일 {(study?.sessionsLast7Days ?? 0).toLocaleString()} · 정확도 {(study?.accuracyRate ?? 0).toFixed(1)}%
                            <br />마지막 학습 {fmtDateOnly(study?.lastStudiedAt)}
                        </Typography>
                    }
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KpiCard
                    label="미션"
                    value={user.missionClaimCount.toLocaleString()}
                    suffix="회 수령"
                    extra={
                        <Typography variant="caption" color="text.secondary">
                            최근 수령 {fmtDateOnly(user.lastMissionClaimedAt)}
                        </Typography>
                    }
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KpiCard
                    label="스티커북"
                    value={eppi ? (eppi.hatched ? '부화' : '미부화') : '-'}
                    extra={
                        <Typography variant="caption" color="text.secondary">
                            보유 코스튬 {(eppi?.ownedCostumeCount ?? 0).toLocaleString()}개 · 방꾸밈 {eppi?.roomDecorated ? '완료' : '미완료'}
                        </Typography>
                    }
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                {credit ? (
                    <KpiCard
                        label="AI 크레딧 (이번 주)"
                        value={credit.balance.toLocaleString()}
                        suffix={`/ ${credit.grantedAmount.toLocaleString()}`}
                        extra={
                            <Typography variant="caption" color="text.secondary">
                                다음 리셋 {fmtDate(credit.nextResetAt)}
                                {credit.isSubscribed ? ' · 구독' : ' · 무료'}
                            </Typography>
                        }
                    />
                ) : (
                    <KpiCard
                        label="AI 크레딧"
                        value="-"
                        extra={<Typography variant="caption" color="text.secondary">지급 이력 없음</Typography>}
                    />
                )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KpiCard
                    label="구독"
                    value={<Chip size="small" label={SUBSCRIPTION_TYPE_LABELS[user.subscriptionType]} color={user.isSubscriptionActive ? 'success' : 'default'} />}
                    extra={
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            {user.isSubscriptionActive ? '활성' : '비활성'} · 만료 {user.subscriptionExpiresAt ? fmtDate(user.subscriptionExpiresAt) : '-'}
                        </Typography>
                    }
                />
            </Grid>
        </Grid>
    );
};

export default ActivitySummaryStrip;
